import type { CSS } from '../stitches.config';
import { keyframes } from '../stitches.config';

export const slideUpAndFade = keyframes({
  '0%': { opacity: 0, transform: 'translateY(2px)' },
  '100%': { opacity: 1, transform: 'translateY(0)' },
});

export const slideRightAndFade = keyframes({
  '0%': { opacity: 0, transform: 'translateX(-2px)' },
  '100%': { opacity: 1, transform: 'translateX(0)' },
});

export const slideDownAndFade = keyframes({
  '0%': { opacity: 0, transform: 'translateY(-2px)' },
  '100%': { opacity: 1, transform: 'translateY(0)' },
});

export const slideLeftAndFade = keyframes({
  '0%': { opacity: 0, transform: 'translateX(2px)' },
  '100%': { opacity: 1, transform: 'translateX(0)' },
});

export const POPUP_TRANSITION = 'opacity 130ms ease-out, transform 130ms ease-out';

const POPUP_SIDES = ['top', 'right', 'bottom', 'left'] as const;
type PopupSide = (typeof POPUP_SIDES)[number];

const POPUP_HIDDEN_TRANSFORMS = {
  top: 'translateY(2px) scale(0.98)',
  right: 'translateX(-2px) scale(0.98)',
  bottom: 'translateY(-2px) scale(0.98)',
  left: 'translateX(2px) scale(0.98)',
} as const satisfies Record<PopupSide, string>;

type PopupMotionSelectors = {
  hidden: string;
  sides: Record<PopupSide, string>;
  transformOrigin: string;
};

const popupMotionStyles = ({ hidden, sides, transformOrigin }: PopupMotionSelectors): CSS => ({
  '--popup-hidden-transform': POPUP_HIDDEN_TRANSFORMS.bottom,
  transformOrigin,

  ...Object.fromEntries(
    POPUP_SIDES.map((side) => [
      sides[side],
      { '--popup-hidden-transform': POPUP_HIDDEN_TRANSFORMS[side] },
    ]),
  ),

  '@media (prefers-reduced-motion: no-preference)': {
    willChange: 'transform, opacity',
    transition: POPUP_TRANSITION,

    [hidden]: {
      opacity: 0,
      transform: 'var(--popup-hidden-transform)',
    },
  },
});

export const baseUIPopupMotionStyles = popupMotionStyles({
  hidden: '&[data-starting-style], &[data-ending-style]',
  sides: {
    top: '&[data-side="top"]',
    right: '&[data-side="right"], &[data-side="inline-end"]',
    bottom: '&[data-side="bottom"]',
    left: '&[data-side="left"], &[data-side="inline-start"]',
  },
  transformOrigin: 'var(--transform-origin)',
});

export const reactAriaPopupMotionStyles = popupMotionStyles({
  hidden: '&[data-entering], &[data-exiting]',
  sides: {
    top: '&[data-placement="top"]',
    right: '&[data-placement="right"]',
    bottom: '&[data-placement="bottom"]',
    left: '&[data-placement="left"]',
  },
  transformOrigin: 'var(--trigger-anchor-point, center)',
});
